import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getBulkOrdersByDate, updateBulkOrders } from 'api/bulkOrders.api';
import { IBulkOrdersState, IBulkOrderUpdateRequest, IBulkOrderWithItems } from 'domain/BulkOrders';

interface ApiError {
  message?: string;
  message_description?: string;
  [key: string]: any;
}

interface ThunkError {
  error: string;
  status?: number;
  data?: ApiError | null;
}

const initialState: IBulkOrdersState = {
  bulk_orders_list: [],
  bulk_orders_list_loaded: false,
};

export const fetchBulkOrdersByDate = createAsyncThunk(
  'bulkOrders/fetchByDate',
  async (params: { selected_date: string, document?: string, workSchedule?: string }, { rejectWithValue }) => {
    try {
      const response = await getBulkOrdersByDate(params);
      return response.data;
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        const errorData: ApiError = err.response?.data || {};
        return rejectWithValue({
          error: errorData.message_description ||
            errorData.message ||
            'Failed to fetch bulk orders',
          status: err.response?.status,
          data: errorData
        } as ThunkError);
      }
      return rejectWithValue({
        error: err instanceof Error ? err.message : 'Unknown error occurred'
      } as ThunkError);
    }
  }
);

export const updateBulkOrderItems = createAsyncThunk(
  'bulkOrders/update',
  async (data: IBulkOrderUpdateRequest, { rejectWithValue }) => {
    try {
      const response = await updateBulkOrders(data);
      return response.data;
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        const errorData: ApiError = err.response?.data || {};
        return rejectWithValue({
          error: errorData.message_description ||
            errorData.message ||
            'Failed to update bulk orders',
          status: err.response?.status,
          data: errorData
        } as ThunkError);
      }
      return rejectWithValue({
        error: err instanceof Error ? err.message : 'Unknown error occurred'
      } as ThunkError);
    }
  }
);

function isAxiosError(error: unknown): error is {
  isAxiosError: boolean;
  response?: {
    status?: number;
    data: ApiError
  }
} {
  return typeof error === 'object' && error !== null && 'isAxiosError' in error;
}

const bulkOrdersSlice = createSlice({
  name: 'bulkOrders',
  initialState,
  reducers: {
    clearBulkOrders: (state) => {
      state.bulk_orders_list = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchBulkOrdersByDate.fulfilled, (state, action) => {
      state.bulk_orders_list_loaded = true;
      state.bulk_orders_list = action.payload.data.map((order: IBulkOrderWithItems) => ({
        ...order,
        recurrent_bulk_order_items: Array.isArray(order.recurrent_bulk_order_items)
          ? order.recurrent_bulk_order_items
          : JSON.parse(order.recurrent_bulk_order_items || '[]'),
      }));
    });
  }
});

export const { clearBulkOrders } = bulkOrdersSlice.actions;

export default bulkOrdersSlice.reducer;